<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="store.loading" />

  <div v-else class="container-fluid my-3">
    <div class="section-header mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
      <span class="section-label">الكورسات</span>
      <button class="btn PrimaryButton" @click="openCreate">
        <i class="pi pi-plus me-1"></i> إضافة كورس
      </button>
    </div>

    <div v-if="!store.courses.length" class="text-center py-5 text-muted">
      <i class="pi pi-inbox" style="font-size:2.5rem;"></i>
      <p class="mt-2">لا توجد كورسات بعد. أضف كورساً جديداً.</p>
    </div>

    <div v-else class="card">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width:70px">الصورة</th>
                <th>الكورس</th>
                <th>المدرّس</th>
                <th>الأسعار</th>
                <th style="width:90px">الظهور</th>
                <th style="width:110px">الاشتراك</th>
                <th style="width:150px">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="course in store.courses" :key="course.id">
                <td>
                  <img v-if="course.image_link" :src="course.image_link" class="course-thumb" alt="" />
                  <span v-else class="course-thumb course-thumb--empty" :style="{ background: course.color || '#e2e8f0' }"></span>
                </td>
                <td>
                  <div class="fw-bold">{{ course.title }}</div>
                  <small class="text-muted">{{ course.slug }}</small>
                </td>
                <td>{{ course.teacher || '—' }}</td>
                <td>
                  <div class="d-flex flex-wrap gap-1">
                    <span v-for="p in coursePlans(course)" :key="p.type" class="badge bg-light text-dark border">
                      {{ p.label }}: {{ money(p.price) }}
                    </span>
                    <span v-if="!coursePlans(course).length" class="text-muted">لا توجد أسعار</span>
                  </div>
                </td>
                <td>
                  <button class="btn btn-sm" :class="course.is_visible ? 'btn-outline-success' : 'btn-outline-secondary'"
                          @click="toggleVisible(course)" :title="course.is_visible ? 'ظاهر' : 'مخفي'">
                    <i :class="course.is_visible ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
                  </button>
                </td>
                <td>
                  <button class="btn btn-sm" :class="course.is_subscription_enabled ? 'btn-success' : 'btn-outline-secondary'"
                          @click="toggleSubscription(course)">
                    {{ course.is_subscription_enabled ? 'مفعّل' : 'معطّل' }}
                  </button>
                </td>
                <td>
                  <button class="btn btn-sm btn-primary me-1" @click="openEdit(course)" title="تعديل">
                    <i class="pi pi-pencil"></i>
                  </button>
                  <button class="btn btn-sm btn-danger" @click="remove(course)" title="حذف">
                    <i class="pi pi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Create / Edit dialog -->
  <Dialog v-model:visible="dialogOpen" modal :style="{ width: '52rem' }" :breakpoints="{ '960px': '95vw' }"
          :header="editing ? 'تعديل كورس' : 'إضافة كورس'">
    <div class="row g-3">
      <div class="col-md-6">
        <label class="form-label">اسم الكورس <span class="text-danger">*</span></label>
        <input type="text" class="form-control" v-model="form.title" placeholder="مثال: مراجعة نهائية جبر" />
      </div>
      <div class="col-md-6">
        <label class="form-label">الرابط (slug)</label>
        <input type="text" class="form-control" v-model="form.slug" placeholder="اتركه فارغاً للتوليد التلقائي" />
      </div>
      <div class="col-md-6">
        <label class="form-label">المدرّس</label>
        <input type="text" class="form-control" v-model="form.teacher" />
      </div>
      <div class="col-md-3">
        <label class="form-label">اللون</label>
        <input type="color" class="form-control form-control-color w-100" v-model="form.color" />
      </div>
      <div class="col-md-3">
        <label class="form-label">الترتيب</label>
        <input type="number" class="form-control" v-model="form.sort_order" min="0" />
      </div>
      <div class="col-12">
        <label class="form-label">الوصف</label>
        <textarea class="form-control" rows="2" v-model="form.description"></textarea>
      </div>

      <div class="col-md-6">
        <label class="form-label">الصورة</label>
        <input type="file" class="form-control" accept="image/*" @change="onImage($event, 'image')" />
        <img v-if="preview.image" :src="preview.image" class="mt-2 rounded" style="max-height:70px" alt="" />
      </div>
      <div class="col-md-6">
        <label class="form-label">صورة الغلاف</label>
        <input type="file" class="form-control" accept="image/*" @change="onImage($event, 'cover_image')" />
        <img v-if="preview.cover_image" :src="preview.cover_image" class="mt-2 rounded" style="max-height:70px" alt="" />
      </div>

      <div class="col-12"><hr class="my-1" /><strong>الأسعار والخطط</strong></div>
      <div class="col-md-3">
        <label class="form-label">سعر الشهر</label>
        <input type="number" class="form-control" v-model="form.monthly_price" min="0" step="0.01" />
      </div>
      <div class="col-md-3">
        <label class="form-label">سعر الترم الأول</label>
        <input type="number" class="form-control" v-model="form.term1_price" min="0" step="0.01" />
      </div>
      <div class="col-md-3">
        <label class="form-label">سعر الترم الثاني</label>
        <input type="number" class="form-control" v-model="form.term2_price" min="0" step="0.01" />
      </div>
      <div class="col-md-3">
        <label class="form-label">السعر السنوي</label>
        <input type="number" class="form-control" v-model="form.yearly_price" min="0" step="0.01" />
      </div>
      <div class="col-md-6">
        <label class="form-label">نهاية الترم الأول</label>
        <input type="date" class="form-control" v-model="form.term1_end_date" />
      </div>
      <div class="col-md-6">
        <label class="form-label">نهاية الترم الثاني</label>
        <input type="date" class="form-control" v-model="form.term2_end_date" />
      </div>

      <div class="col-md-6 d-flex align-items-center gap-2 mt-3">
        <InputSwitch v-model="form.is_visible" inputId="c_visible" />
        <label for="c_visible" class="mb-0">ظاهر في الموقع</label>
      </div>
      <div class="col-md-6 d-flex align-items-center gap-2 mt-3">
        <InputSwitch v-model="form.is_subscription_enabled" inputId="c_sub" />
        <label for="c_sub" class="mb-0">تفعيل الاشتراك</label>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-secondary" @click="dialogOpen = false">إلغاء</button>
      <button class="btn PrimaryButton" :disabled="store.saving || !form.title.trim()" @click="save">
        <i class="pi pi-check me-1"></i>{{ store.saving ? 'جارٍ الحفظ...' : 'حفظ' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useCourseStore } from '@/stores/useCourseStore.js';
import { PLAN_TYPES, planLabel, formatMoney, apiError } from '@/composables/subscriptionMeta.js';

function emptyForm() {
  return {
    title: '', slug: '', teacher: '', color: '#3b82f6', sort_order: 0, description: '',
    monthly_price: '', term1_price: '', term2_price: '', yearly_price: '',
    term1_end_date: '', term2_end_date: '',
    is_visible: true, is_subscription_enabled: false,
  };
}

export default {
  name: 'CoursesIndex',
  components: { Dialog, InputSwitch, Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'الكورسات', path: '/courses' }],
      dialogOpen: false,
      editing: null,
      form: emptyForm(),
      files: { image: null, cover_image: null },
      preview: { image: null, cover_image: null },
    };
  },
  computed: {
    store() { return useCourseStore(); },
  },
  methods: {
    money: formatMoney,
    coursePlans(course) {
      return PLAN_TYPES
        .map(t => ({ type: t, label: planLabel(t), price: course[`${t}_price`] }))
        .filter(p => p.price !== null && p.price !== undefined && p.price !== '');
    },
    openCreate() {
      this.editing = null;
      this.form = emptyForm();
      this.files = { image: null, cover_image: null };
      this.preview = { image: null, cover_image: null };
      this.dialogOpen = true;
    },
    openEdit(course) {
      this.editing = course;
      this.form = {
        title: course.title ?? '', slug: course.slug ?? '', teacher: course.teacher ?? '',
        color: course.color || '#3b82f6', sort_order: course.sort_order ?? 0,
        description: course.description ?? '',
        monthly_price: course.monthly_price ?? '', term1_price: course.term1_price ?? '',
        term2_price: course.term2_price ?? '', yearly_price: course.yearly_price ?? '',
        term1_end_date: course.term1_end_date ? String(course.term1_end_date).slice(0, 10) : '',
        term2_end_date: course.term2_end_date ? String(course.term2_end_date).slice(0, 10) : '',
        is_visible: !!course.is_visible, is_subscription_enabled: !!course.is_subscription_enabled,
      };
      this.files = { image: null, cover_image: null };
      this.preview = { image: course.image_link, cover_image: course.cover_image_link };
      this.dialogOpen = true;
    },
    onImage(event, field) {
      const file = event.target.files[0];
      if (!file) return;
      this.files[field] = file;
      this.preview[field] = URL.createObjectURL(file);
    },
    buildFormData() {
      const fd = new FormData();
      const f = this.form;
      fd.append('title', f.title.trim());
      if (f.slug.trim()) fd.append('slug', f.slug.trim());
      fd.append('teacher', f.teacher ?? '');
      fd.append('color', f.color ?? '');
      fd.append('sort_order', f.sort_order || 0);
      fd.append('description', f.description ?? '');
      PLAN_TYPES.forEach(t => fd.append(`${t}_price`, f[`${t}_price`] === '' ? '' : f[`${t}_price`]));
      fd.append('term1_end_date', f.term1_end_date ?? '');
      fd.append('term2_end_date', f.term2_end_date ?? '');
      fd.append('is_visible', f.is_visible ? 1 : 0);
      fd.append('is_subscription_enabled', f.is_subscription_enabled ? 1 : 0);
      if (this.files.image) fd.append('image', this.files.image);
      if (this.files.cover_image) fd.append('cover_image', this.files.cover_image);
      return fd;
    },
    async save() {
      try {
        const fd = this.buildFormData();
        if (this.editing) {
          await this.store.updateCourse(this.editing.id, fd);
        } else {
          await this.store.createCourse(fd);
        }
        this.dialogOpen = false;
        Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
    async toggleVisible(course) {
      try { await this.store.toggleVisible(course.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async toggleSubscription(course) {
      try { await this.store.toggleSubscription(course.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async remove(course) {
      const res = await Swal.fire({
        title: 'حذف الكورس', html: `سيتم حذف <strong>${course.title}</strong> نهائياً.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.deleteCourse(course.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.store.fetchCourses();
  },
};
</script>

<style scoped>
.course-thumb {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  object-fit: cover;
  display: inline-block;
}
.course-thumb--empty {
  border: 1px solid #e2e8f0;
}
</style>
